<template>
  <div class="funnel" :class="{ 'funnel--fill': fill }">
    <div class="funnel-body">
      <div v-for="(row, i) in rows" :key="'FS' + row.step_id" class="funnel-stage" :title="stageTitle(row, i)">
        <div class="funnel-label">{{ row.label }}</div>
        <div class="funnel-track">
          <div class="funnel-bar" :style="barStyle(row, i)"></div>
          <span class="funnel-count">{{ formatNumber(row.value) }}</span>
          <span class="funnel-share">{{ shareOfFirst(row) }}%</span>
          <span v-if="i > 0 && dropOff(row, i) > 0" class="funnel-drop">{{ dropOff(row, i) }}% drop</span>
        </div>
      </div>
      <div v-if="rows.length === 0" class="funnel-empty">No carts in this period</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FunnelChart',
  props: {
    // Rows from the funnel branch, in stage order: { step_id, label, value }
    chartData: {
      type: Array,
      required: true,
    },
    fill: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: '',
    },
  },
  computed: {
    rgb() {
      // Widget highlight color as "r, g, b" for rgba() mixes; falls back to the brand primary.
      const hex = /^#[0-9a-fA-F]{6}$/.test(this.color || '') ? this.color : '#e14eca';
      return [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)).join(', ');
    },
    rows() {
      return (this.chartData || []).map(r => ({ step_id: Number(r.step_id), label: r.label, value: Number(r.value) || 0 }));
    },
    first() {
      return this.rows.length > 0 ? this.rows[0].value : 0;
    },
  },
  methods: {
    formatNumber(n) {
      const group = JSON.parse(localStorage.getItem('group'));
      const locale = group && group.default_locale ? group.default_locale : undefined;
      return new Intl.NumberFormat(locale).format(n);
    },
    shareOfFirst(row) {
      if (!this.first) return 0;
      return Math.round((row.value / this.first) * 100);
    },
    dropOff(row, i) {
      const prev = this.rows[i - 1] ? this.rows[i - 1].value : 0;
      if (!prev) return 0;
      return Math.max(0, Math.round(((prev - row.value) / prev) * 100));
    },
    barStyle(row, i) {
      const width = this.first ? Math.max(3, (row.value / this.first) * 62) : 0;
      // Deepen the primary as the funnel narrows so the converted stage reads hottest.
      const alpha = 0.55 + (i / Math.max(1, this.rows.length - 1)) * 0.45;
      return { width: width + '%', background: 'rgba(' + this.rgb + ', ' + alpha.toFixed(2) + ')', boxShadow: '0 0 10px rgba(' + this.rgb + ', 0.18)' };
    },
    stageTitle(row, i) {
      const drop = i > 0 ? ', ' + this.dropOff(row, i) + '% dropped off from the previous stage' : '';
      return row.label + ': ' + this.formatNumber(row.value) + ' (' + this.shareOfFirst(row) + '% of visits' + drop + ')';
    },
  },
};
</script>

<style scoped>
.funnel {
  width: 100%;
}
.funnel--fill {
  height: 100%;
  display: flex;
  align-items: safe center;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
}
/* The funnel keeps a readable width on wide tiles instead of stretching edge to edge. */
.funnel-body {
  width: 100%;
  max-width: 880px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
  max-height: 100%;
}
/* Stages share the tile height: they shrink on short tiles and cap on tall ones. */
.funnel-stage {
  display: grid;
  grid-template-columns: minmax(110px, 22%) minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  flex: 1 1 0;
  min-height: 16px;
  max-height: 40px;
}
.funnel-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.white-content .funnel-label {
  color: rgba(0, 0, 0, 0.75);
}
.funnel-track {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  height: 100%;
}
.funnel-bar {
  height: 100%;
  min-height: 14px;
  max-height: 30px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  flex: 0 0 auto;
  transition: width 0.3s ease;
}
.funnel-count {
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
}
.white-content .funnel-count {
  color: #1e1e2f;
}
.white-content .funnel-drop {
  color: #d43d73;
}
.funnel-share {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
}
.white-content .funnel-share {
  color: rgba(0, 0, 0, 0.75);
}
.funnel-drop {
  font-size: 11px;
  color: #fd5d93;
  white-space: nowrap;
}
.funnel-empty {
  text-align: center;
  color: rgba(125, 125, 125, 0.9);
  font-size: 12px;
}
</style>
